import { useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	FormControlLabel,
	MenuItem,
	Select,
	Switch,
	Typography,
} from '@mui/material'
import { CheckCircleOutline, People } from '@mui/icons-material'
import { useDemoMode } from '../../contexts/demo-mode-context'
import { useAuth } from '../../contexts/auth-context'

export function MyAssignmentView({ user, customerId, customers, onAssign, operationLoading }) {
	const { demoMode, setDemoMode } = useDemoMode()
	const { isAllScope, setViewScope } = useAuth()
	const [selectedId, setSelectedId] = useState('')
	const [feedback, setFeedback] = useState(null)

	const currentCustomer = useMemo(
		() => customers.find(c => String(c.id) === String(customerId)) || null,
		[customerId, customers]
	)

	useEffect(() => {
		if (isAllScope) {
			setSelectedId('all')
		} else {
			setSelectedId(customerId ? String(customerId) : '')
		}
	}, [customerId, isAllScope])

	const isUnchanged =
		(selectedId === 'all' && isAllScope) ||
		(selectedId !== 'all' && String(selectedId) === String(customerId) && !isAllScope)

	const handleSubmit = async (e) => {
		e.preventDefault()
		setFeedback(null)

		if (selectedId === 'all') {
			setViewScope('all')
			setFeedback({ severity: 'success', message: 'Now viewing all customers. Customer pages show data across all accounts.' })
			return
		}

		setViewScope(null)
		try {
			await onAssign(selectedId)
			setFeedback({ severity: 'success', message: 'Assignment updated. Customer-scoped pages now reflect this customer.' })
		} catch (err) {
			setFeedback({ severity: 'error', message: `Failed to update assignment: ${err.message}` })
		}
	}

	return (
		<Box sx={{ maxWidth: 600 }}>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
				Assign your admin account to a customer to access customer-scoped views (sensors, historical data, issues) without signing out of the admin portal.
			</Typography>

			<Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
				<CardContent>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>CURRENT ACCOUNT</Typography>
					<Typography variant="body1" fontWeight="bold">{user?.email}</Typography>
					<Box display="flex" alignItems="center" gap={1} mt={1}>
						{isAllScope ? (
							<>
								<People fontSize="small" color="primary" />
								<Typography variant="body2">
									Viewing <strong>All Customers</strong>
								</Typography>
							</>
						) : currentCustomer ? (
							<>
								<CheckCircleOutline fontSize="small" color="success" />
								<Typography variant="body2">
									Assigned to <strong>{currentCustomer.name}</strong>
								</Typography>
							</>
						) : (
							<Typography variant="body2" color="text.secondary">No customer assignment</Typography>
						)}
					</Box>
					<FormControlLabel
						control={
							<Switch
								checked={demoMode}
								onChange={(_, v) => setDemoMode(v)}
								disabled={isAllScope}
								inputProps={{ 'aria-label': 'Demo mode: hide sensor IDs and locations on customer pages' }}
							/>
						}
						label="Demo mode (hide sensor IDs & locations)"
						sx={{ mt: 2, alignItems: 'flex-start', mr: 0 }}
					/>
					<Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4.5, mt: -0.5 }}>
						{isAllScope
							? 'Demo mode is disabled while viewing all customers.'
							: 'When on, the Real-Time Dashboard, Sensor Overview, and Historical pages show real sensor ids with placeholder locations (Demo Water 1, 2, …). Issues still hide sensor text behind blocks. Dashboard and Historical use a cfg param in the URL, not the device id string; links are still sensitive.'}
					</Typography>
				</CardContent>
			</Card>

			{feedback && (
				<Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
					{feedback.message}
				</Alert>
			)}

			<Box component="form" onSubmit={handleSubmit}>
				<Select
					fullWidth
					displayEmpty
					value={selectedId}
					onChange={(e) => {
						setSelectedId(e.target.value)
						setFeedback(null)
					}}
					renderValue={(v) => {
						if (v === 'all') return 'All Customers'
						return v ? customers.find(c => String(c.id) === String(v))?.name : 'Select a customer'
					}}
					sx={{ mb: 2 }}
					inputProps={{ 'aria-label': 'Assigned Customer' }}
				>
					<MenuItem value="" disabled>Select a customer</MenuItem>
					<MenuItem value="all">All Customers</MenuItem>
					{customers.map(c => (
						<MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
					))}
				</Select>
				<Button
					type="submit"
					variant="contained"
					disabled={
						operationLoading === 'assign-current-user' ||
						!selectedId ||
						isUnchanged
					}
				>
					{operationLoading === 'assign-current-user' ? 'Updating…' : 'Update My Assignment'}
				</Button>
			</Box>
		</Box>
	)
}
